import Title_ru_RU               from "@/core/misc/decorators/Title_ru_RU";
import Title_ru_RU_Class         from "@/core/misc/decorators/Title_ru_RU_Class";
import serializeSimplified       from "@/core/misc/decorators/SerializeSimplified";
import TYPES                     from '@/core/services/Changelogs/types';

import AbstractDocumentPosition  from '@/domains/Abstract/AbstractDocumentPosition';
import OrderPosition             from "@/domains/Orders/OrderPosition";
import Core                      from "@/core/Core";

import Qty                       from '@/core/types/Qty';
import VatRate                   from '@/core/types/VatRate';
import Price                     from '@/core/types/Price';
import Sum                       from '@/core/types/Sum';
import CurrencyRate              from '@/core/types/CurrencyRate';

import type PaymentRequest       from "@/domains/PaymentRequests/PaymentRequest";
import type ShipmentPosition     from "@/domains/Shipments/ShipmentPosition";

/**
 * Позиция заявки на платёж.
 *
 * Зеркало PaymentRequestPosition.php: те же формулы, тот же порядок, та же
 * развилка по валютности. Обе стороны считают независимо, бэк сверяет свой
 * результат с присланным до знака.
 */
@Title_ru_RU_Class('Позиция заявки на оплату')
class PaymentRequestPosition extends AbstractDocumentPosition {

    /** Платёж-владелец позиции, ставится через setPaymentRequest.
     *  serializeSimplified обязателен: полный объект зациклил бы сериализацию
     *  (платёж -> позиции -> платёж). */
    @serializeSimplified
    private _paymentRequest: PaymentRequest | null = null;

    /** Ссылка на позицию отгрузки */
    @serializeSimplified
    public shipmentPosition: ShipmentPosition;

    // ---- Снимок основания ----
    // Копия величин позиции-основания на момент создания платежа. Даёт
    // «оплачено 300 кг из 1000» и долю оплаты. После создания не меняется:
    // бэк не пишет эти колонки в updatePosition.

    @Title_ru_RU('Количество по основанию', TYPES.NUMBER)
    public basisQty: Qty;

    /** Базовая сумма в трактовке документа: при НДС сверху она без
    налога, при НДС в цене — уже с ним. Ровно так же устроен sumSeller
    в позиции заказа, откуда снимок и берётся. */
    @Title_ru_RU('Сумма основания', TYPES.SUM)
    public basisSumSeller: Sum;

    // ---- Валюта основания ----

    public orderCurrencyCode: string;

    /* Цена поставщика */
    public priceSeller: Price;
    /* Ставка НДС поставщика */
    public vatRateSeller: VatRate | null;
    /* НДС включён в цену поставщика */
    public isVatIncludedSeller: boolean | null;

    /** Цена с НДС. Поле СПРАВОЧНОЕ: при «НДС сверху» использовать его как
     *  основу расчёта нельзя (теряется точность), при «НДС в т.ч.» оно не
     *  нужно. Считается и хранится для отображения и контракта с бэком. */
    public priceFullSeller: Price;

    @Title_ru_RU('Сумма в валюте основания', TYPES.SUM)
    public sumSeller: Sum = Sum.zero();

    /** Полная сумма в валюте основания — ВЕДУЩАЯ величина позиции.
     *
     *  Платёж — документ про деньги: сколько платим, известно из счёта или из
     *  неоплаченного остатка, а количество из этого выводится. Раньше ведущим
     *  было количество, и чтобы заплатить половину, приходилось считать её в
     *  килограммах.
     *
     *  Именно в валюте основания, а не платежа: курс выводится из отношения
     *  суммы счёта к этой сумме, то есть она известна раньше курса. */
    @Title_ru_RU('Полная сумма в валюте основания', TYPES.SUM)
    private _sumFullSeller: Sum = Sum.zero();

    @Title_ru_RU('НДС в валюте основания', TYPES.SUM)
    public vatSumSeller: Sum = Sum.zero();

    // ---- Валюта платежа ----

    public paymentRequestCurrencyCode: string;

    /* Курс валюты платежа к валюте основания. Ставится платежом */
    private _paymentRequestCurrencyRate: CurrencyRate;

    /* Цена в валюте платежа. Расчётная */
    public paymentRequestPrice: Price;
    /* Цена полная в валюте платежа. Справочная, см. priceFullSeller */
    public paymentRequestPriceFull: Price;

    @Title_ru_RU('Сумма в валюте платежа', TYPES.SUM)
    public paymentRequestSum: Sum = Sum.zero();

    @Title_ru_RU('НДС в валюте платежа', TYPES.SUM)
    public paymentRequestVatSum: Sum = Sum.zero();

    /** Единственное вводимое пользователем поле позиции — правится в гриде
     *  карточки. В базу расчёта не входит: лежит рядом с эталоном, сверяется
     *  с ним и уходит в платёжный процессинг через ??. */
    @Title_ru_RU('Полная сумма в валюте платежа', TYPES.SUM)
    private _paymentRequestSumFull: Sum | null = null;

    @Title_ru_RU('Полная сумма в валюте платежа (рассчитанная автоматически)', TYPES.SUM)
    private _paymentRequestSumFullCalculated: Sum = Sum.zero();

    /* ────────────────────────────────────────
       Аксессоры
       ──────────────────────────────────────── */

    get sumFullSeller(): Sum {
        return this._sumFullSeller;
    }

    set sumFullSeller(value: Sum) {
        this._sumFullSeller = value;
        this.calculate();
    }

    /** Количество живёт в базовом классе как _qty. Здесь переопределяется
     *  только сеттер — ради каскада пересчёта. Объявить поле qty заново
     *  нельзя: при useDefineForClassFields объявление создаёт собственное
     *  свойство и навсегда перекрывает прототипный аксессор. */
    get qty(): Qty {
        return this._qty;
    }

    /** Количество производно, но вводить его по-прежнему можно: это просто
     *  второй способ задать сумму. Присваивание переводится в деньги по цене
     *  с НДС, дальше работает обычный каскад, и количество возвращается из
     *  расчёта — возможно, на знак отличным от введённого. */
    set qty(qty: Qty) {
        if (Core.utils.isNullOrUndefined(this.priceFullSeller) === true) {
            this._qty = qty;
        }
        else {
            this.sumFullSeller = this.priceFullSeller.mul(qty);
        }
    }

    /** Полная сумма основания — снимок в той же трактовке, что и суммы
     *  позиции. Нужна проценту оплаты: доля берётся от полной суммы. */
    get basisSumFullSeller(): Sum {
        return this.getFullSum(this.basisSumSeller);
    }

    get paymentRequestSumFull(): Sum | null {
        return this._paymentRequestSumFull;
    }

    /** Ввод пользователя. Собственный расчёт позиции не трогает — только итог
     *  документа, куда введённое значение попадает вместо расчётного.
     *
     *  Платёж оповещается отдельным методом, а не recalculateSums напрямую:
     *  при оплате в точности по счёту введённая сумма задаёт ещё и курс, и
     *  решает это документ, а не позиция. */
    set paymentRequestSumFull(value: Sum | null) {
        this._paymentRequestSumFull = value;
        this.applySumFullSellerFromPayment(value);
        this._paymentRequest?.onPositionSumEntered();
    }

    /**
     * Переводит введённую сумму платежа в валюту основания и кладёт её в
     * ведущее поле. Дальше работает обычный каскад: из суммы основания
     * считаются НДС, количество и весь блок валюты платежа.
     *
     * Введённое значение при этом остаётся лежать отдельно и в расчёт не
     * входит: суммы округляются до двух знаков, а курс до восьми, и обратный
     * проход съел бы копейки введённого. В итог документа идёт именно оно.
     */
    private applySumFullSellerFromPayment(value: Sum | null): void {
        if (value === null || value.isPositive() === false) {
            return;
        }
        else if (this.isSameCurrency === true) {
            this.sumFullSeller = value;
        }
        else if (this._paymentRequestCurrencyRate?.isPositive() !== true) {
            // Курса нет — переводить нечем, ждём его появления
        }
        else {
            this.sumFullSeller = Sum.fromOperation(value.value.div(this._paymentRequestCurrencyRate.value));
        }
    }

    get paymentRequestSumFullCalculated(): Sum {
        return this._paymentRequestSumFullCalculated;
    }

    get paymentRequestCurrencyRate(): CurrencyRate {
        return this._paymentRequestCurrencyRate;
    }

    set paymentRequestCurrencyRate(rate: CurrencyRate) {
        this._paymentRequestCurrencyRate = rate;
        this.calculatePaymentRequest();
    }

    /** Полная сумма к показу и к отправке в процессинг: введённое, иначе
     *  расчётное. Зеркало Sum::coalesce на беке. */
    get paymentRequestSumFullEffective(): Sum {
        if (this._paymentRequestSumFull === null) {
            return this._paymentRequestSumFullCalculated;
        }
        else {
            return this._paymentRequestSumFull;
        }
    }

    /** Отклонение введённой суммы от расчётной больше допуска — повод
     *  предупредить пользователя, но не ошибка: курсовая разница и предоплаты
     *  дают законное расхождение. */
    get hasSumDeviation(): boolean {
        if (this._paymentRequestSumFull === null) {
            return false;
        }
        else if (this._paymentRequestSumFullCalculated.isZero() === true) {
            return false;
        }
        else {
            /** divBySum даёт Multiplier, а его asPercent — это ровно отклонение
             *  от базы в процентах: 1.013 → 1.3. Для сравнения с допуском
             *  именно то, что нужно. */
            const deviation = this._paymentRequestSumFull.divBySum(this._paymentRequestSumFullCalculated);

            return deviation.asPercent().abs().greaterThan(PaymentRequestPosition.SUM_MAX_DEVIATION_PERCENT);
        }
    }

    /** Допуск расхождения ввода с расчётом, в процентах. Совпадает с порогом
     *  одноимённого ограничения в БД (там доля, здесь проценты). */
    private static readonly SUM_MAX_DEVIATION_PERCENT = 1;

    /* ────────────────────────────────────────
       Гидрация
       ──────────────────────────────────────── */

    static fromServer(data: any, result: PaymentRequestPosition = new PaymentRequestPosition()): PaymentRequestPosition {
        super.fromServer(data, result);

        result.orderPosition = Core.utils.hydrate(data.orderPosition, OrderPosition.fromServer)!;

        result.basisQty       = Core.utils.hydrate(data.basisQty, Qty.fromServer)!;
        result.basisSumSeller = Core.utils.hydrate(data.basisSumSeller, Sum.fromServer)!;

        result.vatRateSeller   = Core.utils.hydrate(data.vatRateSeller, VatRate.fromServer);
        result.priceSeller     = Core.utils.hydrate(data.priceSeller, Price.fromServer)!;
        result.priceFullSeller = Core.utils.hydrate(data.priceFullSeller, Price.fromServer)!;
        result.sumSeller       = Core.utils.hydrate(data.sumSeller, Sum.fromServer)!;
        // Ведущая сумма — напрямую в поле: сеттер дёрнул бы пересчёт на ещё
        // не заполненных соседях, как и у курса ниже.
        result._sumFullSeller  = Core.utils.hydrate(data.sumFullSeller, Sum.fromServer)!;
        result.vatSumSeller    = Core.utils.hydrate(data.vatSumSeller, Sum.fromServer)!;

        // Курс и ввод кладём напрямую в _protected, минуя сеттеры: сеттер
        // дёрнул бы пересчёт на ещё не заполненных соседях.
        result._paymentRequestCurrencyRate = Core.utils.hydrate(data.paymentRequestCurrencyRate, CurrencyRate.fromServer)!;

        result.paymentRequestPrice     = Core.utils.hydrate(data.paymentRequestPrice, Price.fromServer)!;
        result.paymentRequestPriceFull = Core.utils.hydrate(data.paymentRequestPriceFull, Price.fromServer)!;
        result.paymentRequestSum       = Core.utils.hydrate(data.paymentRequestSum, Sum.fromServer)!;
        result.paymentRequestVatSum    = Core.utils.hydrate(data.paymentRequestVatSum, Sum.fromServer)!;

        result._paymentRequestSumFull           = Core.utils.hydrate(data.paymentRequestSumFull, Sum.fromServer);
        result._paymentRequestSumFullCalculated = Core.utils.hydrate(data.paymentRequestSumFullCalculated, Sum.fromServer)!;

        return result;
    }

    /**
     * Привязывает платёж-владелец. Ставится родителем последним шагом
     * гидрации, а не приходит параметром в fromServer.
     *
     * Платёж по отгрузке — заодно находим соответствующую позицию отгрузки.
     */
    setPaymentRequest(paymentRequest: PaymentRequest): void {
        this._paymentRequest = paymentRequest;

        this.orderCurrencyCode           = paymentRequest.orderCurrencyCode;
        this.paymentRequestCurrencyCode  = paymentRequest.paymentRequestCurrencyCode;
        this._paymentRequestCurrencyRate = paymentRequest.paymentRequestCurrencyRate;

        if (paymentRequest.shipment !== null && paymentRequest.shipment !== undefined) {
            this.shipmentPosition = paymentRequest.shipment.positions.find(
                shipmentPosition => shipmentPosition.orderPosition.id === this.orderPosition.id,
            );
        }

        /** Только что собранную позицию считать было нечем: валюта и курс
         *  приходят отсюда. Гидрированную пересчитывать не надо — её значения
         *  уже пришли с сервера. */
        if (this.sumSeller.isZero() === true) {
            this.calculate();
        }
    }

    /* ────────────────────────────────────────
       Конструирование
       ──────────────────────────────────────── */

    /**
     * Позиция платежа из позиции заказа. Количество по умолчанию — всё
     * количество основания; долю пользователь задаёт потом, количеством,
     * суммой или процентом в шапке.
     *
     * Сигнатура — как у родителя, один параметр. Платёж сюда не передаётся:
     * обратную ссылку ставит сам платёж через setPaymentRequest, и он же
     * запускает расчёт. Здесь считать нечем — валюта и курс живут в шапке.
     */
    static fromOrderPosition(orderPosition: OrderPosition): PaymentRequestPosition {
        const position = new PaymentRequestPosition();

        position.orderPosition = orderPosition;
        position.product       = orderPosition.product;
        position.unit          = orderPosition.unit;

        position.basisQty       = orderPosition.qty;
        position.basisSumSeller = orderPosition.sumSeller;

        position.priceSeller         = orderPosition.priceSeller;
        position.vatRateSeller       = orderPosition.vatRateSeller;
        position.isVatIncludedSeller = orderPosition.isVatIncludedSeller;

        /** Напрямую в поле, а не через сеттер: расчёт всё равно не пройдёт до
         *  setPaymentRequest, а сеттер дёрнул бы его на пустой валюте. */
        position._sumFullSeller = PaymentRequestPosition.getUnpaidSum(orderPosition, orderPosition.sumFullSeller);

        return position;
    }

    /**
     * Сумма, которую ещё можно оплатить. Оплаченное и стоящее в очереди
     * вычтено: по полностью оплаченной позиции платить нечего, а раньше в
     * платёж шло всё количество основания, и получалась вторая оплата.
     *
     * Статуса оплаты может не быть (позиция приехала без него) — тогда
     * ограничивать нечем, берём сумму целиком.
     */
    private static getUnpaidSum(orderPosition: OrderPosition, sumFull: Sum): Sum {
        const paymentStatus = orderPosition.paymentStatus;

        if (Core.utils.isNullOrUndefined(paymentStatus) === true) {
            return sumFull;
        }
        else if (paymentStatus.maxAvailableCurrent.compare(sumFull) === -1) {
            return paymentStatus.maxAvailableCurrent;
        }
        else {
            return sumFull;
        }
    }

    /**
     * Позиция платежа из позиции отгрузки. Отличие от fromOrderPosition одно:
     * количество берётся отгруженное, а не заказанное — платим за то, что
     * реально приехало.
     *
     * Снимок основания остаётся по заказу: он нужен для доли оплаты, а
     * мерой «сколько всего» служит именно заказ.
     */
    static fromShipmentPosition(shipmentPosition: ShipmentPosition): PaymentRequestPosition {
        const position = PaymentRequestPosition.fromOrderPosition(shipmentPosition.orderPosition);

        position.shipmentPosition = shipmentPosition;
        position._sumFullSeller   = PaymentRequestPosition.getUnpaidSum(shipmentPosition.orderPosition, shipmentPosition.sumFull);

        return position;
    }

    /* ────────────────────────────────────────
       Расчёт
       ──────────────────────────────────────── */

    /**
     * Расчёт в валюте основания. Первична СУММА: платёж — документ про
     * деньги, и платят в нём не количеством.
     *
     * Отсюда и направление: из полной суммы снимается НДС, из неё же выводится
     * количество. Цена в расчёт не входит и остаётся справочной — она та же,
     * что в заказе, и не обязана сходиться с суммой до знака. У исторических
     * позиций заказа цена хранилась с пятью знаками (10029157 — 0.20564), а до
     * нас доезжает округлённой до scale Price; при прежнем направлении эта
     * разница множилась на всё количество.
     *
     * Каскадом тянет за собой валюту платежа и итоги документа.
     */
    calculate(): void {
        this.priceFullSeller = this.getFullPrice(this.priceSeller);

        this.sumSeller    = this.getNetSum(this._sumFullSeller);
        this.vatSumSeller = this.getVatSum(this.sumSeller);

        this._qty = this.deriveQty();

        this.calculatePaymentRequest();
    }

    /**
     * Количество из полной суммы и полной цены. Справочное: округляется до
     * трёх знаков, и обратно в сумму в общем случае не сходится.
     *
     * Пустая или нулевая цена — количество нулевое: делить не на что, а
     * расчёт дёргается и на недозаполненной форме.
     */
    private deriveQty(): Qty {
        if (Core.utils.isNullOrUndefined(this.priceFullSeller) === true) {
            return Qty.zero();
        }
        else if (this.priceFullSeller.isPositive() === false) {
            return Qty.zero();
        }
        else {
            return this._sumFullSeller.divByPrice(this.priceFullSeller);
        }
    }

    /** Валюта основания совпадает с валютой платежа: курс не участвует */
    get isSameCurrency(): boolean {
        return this.orderCurrencyCode === this.paymentRequestCurrencyCode;
    }

    /**
     * Расчёт в валюте платежа.
     *
     * Совпали валюты — считаем слева направо: цена первична, сумма из неё.
     * Разошлись — направление обратное, первична СУММА по курсу, цена из неё
     * производна. Умножать цену на курс и потом на количество нельзя:
     * округление цены до scale размножилось бы на всё количество. Обратной
     * сходимости цена × количество = сумма в валютном случае нет.
     *
     * Введённая пользователем полная сумма сюда не входит — она сверяется
     * с результатом, а не подменяет его.
     */
    calculatePaymentRequest(): void {
        /** Курс приезжает асинхронно: до его появления считать не из чего.
         *  Пересчёт вызовет сеттер paymentRequestCurrencyRate. */
        if (this.isSameCurrency === false && Core.utils.isNullOrUndefined(this._paymentRequestCurrencyRate)) {
            return;
        }

        if (this.isSameCurrency === true) {
            /** Валюта та же — конвертировать нечего, сумма переезжает как
             *  есть. Цена справочна и берётся у продавца. */
            this.paymentRequestPrice = this.priceSeller;
            this.paymentRequestSum   = this.sumSeller;
        }
        else {
            this.paymentRequestSum   = this.sumSeller.mul(this._paymentRequestCurrencyRate);
            this.paymentRequestPrice = this.derivePrice(this.paymentRequestSum);
        }

        this.paymentRequestPriceFull          = this.getFullPrice(this.paymentRequestPrice);
        this._paymentRequestSumFullCalculated = this.getFullSum(this.paymentRequestSum);
        this.paymentRequestVatSum             = this.getVatSum(this.paymentRequestSum);

        this._paymentRequest?.recalculateSums();
    }

    /**
     * Цена, выведенная из суммы. Валютное основание читается справа налево:
     * сумма первична, цена производна и справочна.
     */
    private derivePrice(sum: Sum): Price {
        if (this.qty.isZero() === true) {
            return Price.zero();
        }
        else {
            return sum.div(this.qty);
        }
    }

    /* ────────────────────────────────────────
       Общие формулы НДС (одни на обе валюты)
       ──────────────────────────────────────── */

    /** Цена с НДС. Ветвление по признаку включения — внутри getFull, она одна
     *  на весь фронт; здесь только отсечение случаев, когда налога нет. */
    private getFullPrice(price: Price): Price {
        if (this.vatRateSeller === null) {
            return price;
        }
        else if (this.isVatIncludedSeller === null) {
            return price;
        }
        else {
            return price.getFull(this.isVatIncludedSeller, this.vatRateSeller);
        }
    }

    /** Сумма без НДС из полной — обратная getFullSum. Нужна инверсии: ведущей
     *  величиной стала полная сумма, а базовая выводится из неё. */
    private getNetSum(sum: Sum): Sum {
        if (this.vatRateSeller === null) {
            return sum;
        }
        else if (this.isVatIncludedSeller === null) {
            return sum;
        }
        else {
            return sum.getNet(this.isVatIncludedSeller, this.vatRateSeller);
        }
    }

    /** Полная сумма. См. getFullPrice. */
    private getFullSum(sum: Sum): Sum {
        if (this.vatRateSeller === null) {
            return sum;
        }
        else if (this.isVatIncludedSeller === null) {
            return sum;
        }
        else {
            return sum.getFull(this.isVatIncludedSeller, this.vatRateSeller);
        }
    }

    /**
     * Сумма налога.
     *
     * Комбинация «ставка задана, признак включения пуст» отдаёт ноль, а не
     * начисляет налог. Комбинация запрещена ограничением в БД, ветка оставлена
     * как страховка.
     */
    private getVatSum(sum: Sum): Sum {
        if (this.vatRateSeller === null) {
            return Sum.zero();
        }
        else if (this.isVatIncludedSeller === null) {
            return Sum.zero();
        }
        else {
            return sum.getVat(this.isVatIncludedSeller, this.vatRateSeller);
        }
    }
}

export default PaymentRequestPosition;